import BaseSlideout from '../slideout';
import Button, { ButtonState, ButtonType } from '../../button/Button';
import { IconType } from '../../icon/Icon';
import { useState } from 'react';

import './quickView.scss';
import CopyCode from '../../link/variants/copyCode';

type Attribute = {
  label: string;
  displayName?: string;
  value: string | boolean;
  key: string;
};

type QuickViewPanelProps = {
  title: string;
  image?: string;
  systemCode?: string;
  attributes?: Attribute[];
  featuredLabels?: string[];
  saved?: boolean;
  isVisible: boolean;
  onClose?: () => void;
  onViewDetails?: () => void;
  isLoading?: boolean;
  errorMessage?: string;
  isLoggedIn?: boolean;
  smartListerLabels?: any;
};

const QuickViewPanel = ({
  title,
  image,
  systemCode,
  attributes = [],
  featuredLabels = [],
  saved = false,
  isVisible,
  onClose,
  onViewDetails,
  isLoading,
  errorMessage,
  isLoggedIn,
  smartListerLabels = {},
}: QuickViewPanelProps) => {
  const [isSaved, setIsSaved] = useState(saved);
  const toggleSave = () => setIsSaved(!isSaved);

  const filteredAttributes = attributes.filter((attr) => {
    // Filter out attributes that are not in the featuredLabels
    if (featuredLabels.length > 0) {
      return featuredLabels.includes(attr.key);
    }
    // If no featuredLabels, return all attributes
    return true;
  });

  return (
    <BaseSlideout
      title={title}
      isVisible={isVisible}
      onClose={onClose}
      isLoading={isLoading}
      errorMessage={errorMessage}
      className="quick-view"
    >
      {isLoading && (
        <>
          <div className="quick-view-body">
            <div className="slideout-skeleton">
              <div className="skeleton-header" />
            </div>
            <hr className="divider" />
            <div className="scrollable-content">
              <div className="slideout-skeleton">
                <div className="skeleton-image" />
                <div className="skeleton-line short" />
                <div className="skeleton-line" />
                <div className="skeleton-line" />
                <div className="skeleton-attribute" />
                <div className="skeleton-attribute" />
                <div className="skeleton-footer-btn" />
              </div>
            </div>
          </div>
          <div className="quick-view-footer">
            <hr className="divider" />
            <div className="slideout-skeleton">
              <div className="skeleton-footer-btn" />
              <div className="skeleton-footer-btn" />
            </div>
          </div>
        </>
      )}
      {!isLoading && !errorMessage && (
        <>
          <div className="slideout-body">
            {systemCode && (
              <CopyCode
                code={systemCode}
                smartListerLabels={smartListerLabels}
              />
            )}
            <hr className="divider" />
            <div className="scrollable-content">
              {image && <img className="info-image" src={image} alt={title} />}
              {filteredAttributes.length > 0 && (
                <div className="attributes-section">
                  <h6>
                    {smartListerLabels.technicalPropertiesTitle ||
                      'Technical Data & Properties'}
                  </h6>
                  <div className="attributes-table">
                    {filteredAttributes.map((attr, index) => {
                      const label = attr.displayName || attr.key;
                      const value =
                        typeof attr.value === 'boolean'
                          ? attr.value
                            ? '✔'
                            : '✘'
                          : attr.value;
                      return (
                        <div
                          className={`attribute-row ${
                            index % 2 === 0 ? 'even' : 'odd'
                          }`}
                          key={index}
                        >
                          <p>
                            <strong>{label}</strong>
                          </p>
                          <p className="u-text-small">{value}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="slideout-footer quick-view-footer">
            <hr className="divider" />
            <Button
              type={ButtonType.SECONDARY}
              label={
                isSaved && isLoggedIn
                  ? smartListerLabels?.unSaveSystemFavoritesHover ||
                    'Unsave system'
                  : smartListerLabels?.saveSystemFavoritesHover || 'Save system'
              }
              icon={
                isSaved && isLoggedIn
                  ? IconType.FavoritesAdded
                  : IconType.Favorites
              }
              state={
                isSaved && isLoggedIn ? ButtonState.ACTIVE : ButtonState.DEFAULT
              }
              onClick={toggleSave}
              aria-pressed={isSaved}
              extraClasses="bottom-button"
              tooltip={
                isLoggedIn
                  ? undefined
                  : {
                      content: 'You need to login to use this functionality',
                      interactive: true,
                      maxWidth: 350,
                    }
              }
            />
            <Button
              type={ButtonType.PRIMARY}
              label={
                smartListerLabels?.viewProductDetail || 'View product details'
              }
              onClick={onViewDetails}
              extraClasses="bottom-button"
            />
          </div>
        </>
      )}
    </BaseSlideout>
  );
};

export default QuickViewPanel;
